import { apiClient, type Paginated } from '@/core/api';
import type { Contact, ContactInput } from './types';

export const BASE = '/crm/contacts/';

export interface ContactListParams {
  page?: number;
  search?: string;
  company?: number;
  /** `true` → only archived rows; `'all'` → both. Unset/`false` → active only. */
  archived?: boolean | 'all';
  /** DRF `OrderingFilter` — e.g. `last_name` or `-last_name`. */
  ordering?: string;
  /** Filter to rows carrying this collab tag id. */
  tag?: number;
}

export async function listContacts(params: ContactListParams = {}): Promise<Paginated<Contact>> {
  const { data } = await apiClient.get<Paginated<Contact>>(BASE, { params });
  return data;
}

export async function getContact(id: string | number): Promise<Contact> {
  const { data } = await apiClient.get<Contact>(`${BASE}${id}/`);
  return data;
}

export async function createContact(input: ContactInput): Promise<Contact> {
  const { data } = await apiClient.post<Contact>(BASE, input);
  return data;
}

export async function updateContact(id: string | number, input: Partial<ContactInput>): Promise<Contact> {
  const { data } = await apiClient.patch<Contact>(`${BASE}${id}/`, input);
  return data;
}

/**
 * Soft-archive a contact. The backend keeps the row, flips `is_archived=true`,
 * and hides it from default list queries. Use {@link restoreContact} to reverse.
 */
export async function archiveContact(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function restoreContact(id: string | number): Promise<Contact> {
  const { data } = await apiClient.post<Contact>(`${BASE}${id}/restore/`);
  return data;
}

/** Permanently delete an archived contact (hard delete; admin only, irreversible). */
export async function purgeContact(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/purge/`);
}
